/**
 * Fichier : chat_widget/src/widget/hooks/useChatPanel.ts
 *
 * Principe :
 * Ce hook est le point d'orchestration principal du widget de chat.
 *
 * Il ne contient pas de logique métier lourde mais compose plusieurs hooks spécialisés :
 *
 * - useChatSession     → gestion de l'identité utilisateur et de la session
 * - useChatMessages    → gestion des messages + Socket.IO (temps réel)
 * - useChatTyping      → indicateur de saisie côté client
 * - useChatSender      → envoi REST des messages vers l'API
 * - useChatForms       → gestion et persistance des formulaires conversationnels
 * - useVehicleActions  → actions spécifiques liées aux véhicules
 *
 * Objectif architectural :
 * Garder ce fichier léger et lisible en séparant clairement les responsabilités.
 * `useChatPanel` assemble les briques et expose une API simple à l'UI.
 */
type Params = {
  text: string;
  carId?: string;
};

import { useRef, useState, useCallback, useEffect } from "react";

import { useChatSession } from "./useChatSession";
import { useChatMessages } from "./useChatMessages";
import { useAutoScrollToBottom } from "./useAutoScrollToBottom";
import { useChatTyping } from "./useChatTyping";
import { useChatSender } from "./useChatSender";
import { useChatForms } from "./useChatForms";
import { useVehicleActions } from "./useVehicleActions";
import { API_CHAT_URL, API_URL } from "../../config";
import { useBadge } from "./useBadge";
import type { LeadFormProps } from "../types/formTypes";

export function useChatPanel({
  userId,
  services,
}: {
  userId: string;
  services: [];
}) {
  const [open, setOpen] = useState(false); // on arrive ici après clic → ouvert

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const { effectiveUserId, sessionId, refreshSession } = useChatSession({
    userId,
  });

  const {
    formActive,
    formCarId,
    setFormActive,
    setFormCarId,
    resetForms,
    attachedCar,
    setAttachedCar,
  } = useChatForms({
    effectiveUserId,
    sessionId,
  });

  const {
    selectedCar,
    setSelectedCar,
    clearSelectedCar,
    getVehicleDetails,
    loadingVehicle,
    cancelVehicleRequests,
  } = useVehicleActions({
    sessionId,
    userId: effectiveUserId,
  });

  const {
    messages,
    setMessages,
    assistantWorking,
    setAssistantWorking,
    loading: historyLoading,
    chatIdRef,
    roomIdRef,
    socketRef,
    socketConnected,
    lang,
    agentName,
  } = useChatMessages(sessionId, effectiveUserId || "", open);

  const { showBadge } = useBadge({ userId, sessionId, open, messages });

  const { onUserTyping: emitUserTyping, stopTyping } = useChatTyping({
    socketRef,
    chatIdRef,
    socketConnected,
    userId,
    sessionId,
  });

  useAutoScrollToBottom(bodyRef, [
    messages,
    assistantWorking,
    historyLoading,
    loadingVehicle,
    formActive,
  ]);

  const scrollToBottom = useCallback(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const isBusy = loading || historyLoading;

  const { sendMessage, cancelSendRequests } = useChatSender({
    sessionId,
    userId: effectiveUserId || "",
    setMessages,
    setLoading,
    formActive,
    selectedCar,
    attachedCar,
    clearSelectedCar,
    formCarId,
    setFormActive,
    isBusy,
    stopTyping,
    scrollToBottom,
    socketRef,
    chatIdRef,
    roomIdRef,
  });

  async function onSend(textParams?: string) {
    const text = (textParams ?? input).trim();
    if (!text) return;

    if (!effectiveUserId) return;

    const params: Params = { text };

    // Si une voiture est sélectionnée, elle devient l'attachedCar
    if (selectedCar?._id) {
      setAttachedCar(selectedCar);
      params.carId = selectedCar._id;
      clearSelectedCar();
    }
    // Sinon si une voiture est déjà attachée, on continue avec elle
    else if (attachedCar?._id) {
      params.carId = attachedCar._id;
    }

    setInput("");
    await sendMessage(params);
  }

  async function onMoreInfo(carId: string) {
    // On attache si on a le bon objet sous la main
    const carData = selectedCar;

    if (selectedCar?._id && selectedCar._id === carId) {
      clearSelectedCar();
    }
    setFormCarId(carId);
    setFormActive({
      name: "lead_more_infos_vehicle",
      carName: carData ? `${carData.marque} ${carData.modele}` : "",
      label: carData
        ? `En savoir plus - ${carData.marque} ${carData.modele}`
        : "",
    });
    scrollToBottom();
  }

  async function onMoreInfoGarage() {
    setFormActive({
      name: "lead_more_infos_garage",
      label: "Un vendeur peut vous rappeler !",
    });
    scrollToBottom();
  }

  async function onBookTestDrive(carId: string) {
    if (isBusy) return;
    const carData = selectedCar;
    if (selectedCar?._id && selectedCar._id === carId) {
      clearSelectedCar();
    }
    setAssistantWorking(true);
    const res = await fetch(`${API_CHAT_URL}/chat/garage/schedules`, {
      headers: { "x-user-id": userId },
    });
    const data = await res.json();
    setFormCarId(carId);
    setAssistantWorking(false);
    setFormActive({
      name: "lead_test_drive",
      services,
      schedules: data?.schedule_info,
      carName: carData ? `${carData.marque} ${carData.modele}` : "",
      label: carData
        ? `Réserver un essai ${carData.marque} ${carData.modele}`
        : "",
    });

    scrollToBottom();
  }

  const handleRefresh = () => {
    // Cancel any in-flight requests
    cancelSendRequests();
    cancelVehicleRequests();

    // Reset UI state
    setLoading(false);
    refreshSession();
    clearSelectedCar();
    resetForms();
    setAssistantWorking(false);
  };

  const onUserTyping = useCallback(
    (text: string) => {
      setInput(text);
      emitUserTyping();
    },
    [emitUserTyping],
  );

  const onBooked: LeadFormProps["onBooked"] = async (payload) => {
    const userMessages = messages.filter((el) => el.role === "user");
    const lastUserMessage = userMessages[userMessages.length - 1] ?? null;

    const data = {
      ...payload,
      userId,
      carId: formCarId || null,
      message: lastUserMessage.text,
    };

    if (formActive?.name === "lead_more_infos_garage") {
      await fetch(`${API_URL}/chat/informations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    }
    if (formActive?.name === "lead_more_infos_vehicle") {
      await fetch(`${API_URL}/chat/car/informations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    }
    if (formActive?.name === "lead_test_drive") {
      await fetch(`${API_URL}/chat/car/testdrive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    }
    if (formActive?.name === "lead_garage_trade_in") {
      await fetch(`${API_URL}/chat/reprise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    }
    const leadData = {
      userId,
      prospect: { ...payload, message: lastUserMessage.text },
      objectId: formCarId,
      type: `chat ${formActive?.name === "lead_more_infos_garage" ? "informations" : formActive?.name === "lead_more_infos_vehicle" ? "informations véhicule" : formActive?.name === "lead_test_drive" ? "essai véhicule" : "reprise véhicule"}`,
    };
    await fetch(`${API_URL}/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(leadData),
    });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [open, scrollToBottom]);

  return {
    input,
    attachedCar,
    clearAttachedCar: resetForms,
    setInput: onUserTyping,
    setFormActive,
    loading: assistantWorking,
    formActive,
    messages,
    lang,
    agentName,
    bodyRef,
    onSend,
    selectedCar,
    onMoreInfo,
    onBookTestDrive,
    getVehicleDetails,
    setSelectedCar,
    onMoreInfoGarage,
    refreshSession: handleRefresh,
    scrollToBottom,
    open,
    setOpen,
    showBadge,
    onBooked,
  };
}
